import { useEffect, useRef } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useUserRole } from '@/hooks/useUserRole'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated } from '@/features/auth/authSlice'
import { showValidationMessage } from '@/lib/utils/notifications'
import type { PolicyGuardProps } from './PolicyGuard.types'

/**
 * Route guard that checks if the user has the required policies.
 * Mirrors Angular's AuthGuard + routewithPolicy map from auth.guard.ts.
 */
export function PolicyGuard({
  children,
  requiredPolicies,
  redirectTo = '/dashboard',
  showAccessDenied = false,
}: PolicyGuardProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector((state) => state.auth.user)
  const { hasPolicy } = useUserRole()
  const navigate = useNavigate()
  const deniedPromptedRef = useRef(false)

  const policiesReady = Boolean(isAuthenticated && user?.current_role)
  const allowed = !policiesReady || hasPolicy(requiredPolicies)

  useEffect(() => {
    if (!showAccessDenied || !policiesReady || allowed || deniedPromptedRef.current) return
    deniedPromptedRef.current = true
    // Angular: Validation Message popup + redirect to dashboard (dialog stays on body).
    showValidationMessage('Access Denied', 'Validation Message')
    navigate(redirectTo, { replace: true })
  }, [allowed, navigate, policiesReady, redirectTo, showAccessDenied])

  if (!isAuthenticated) {
    return <>{children}</>
  }

  if (!user || !user.current_role) {
    return <>{children}</>
  }

  if (!hasPolicy(requiredPolicies)) {
    if (showAccessDenied) return null
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}
